/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, Oct 2021
 * @flow
 */

import React from 'react';
import { connect } from 'react-redux';

import View from './views';
import API from '../../requests';
import FireboardUtils from '../../util/fireboard-util';
import { getPopupConfig } from '../../components/Popup';
import EventEmitter from '../../util/event-emitter';

class NamedSensors extends React.Component {
  state = {
    popup: undefined,
    namedSensors: [],
    filteredNamedSensors: [],
    selectedSensorGrpId: null,
    sensorName: '',
    searchKey: '',
  };
  sensorGroupsList = [];
  scanQRCodeListener = null;

  componentDidMount() {
    const { reduxProps: { storeInfo } } = this.props;
    const { id: storeId, sensorGroups } = storeInfo?.data || {};
    this.sensorGroupsList = sensorGroups;
    this.getNamedSensors(storeId);

    if (window.ReactNativeWebView) {
      this.scanQRCodeListener = message => this.onQRCodeScanned(message);
      EventEmitter.addListeners('onQRCodeScanned', this.scanQRCodeListener);
    }
  }

  componentWillUnmount() {
    if (window.ReactNativeWebView) {
      this.scanQRCodeListener && EventEmitter.removeListeners('onQRCodeScanned', this.scanQRCodeListener);
    }
  }

  getNamedSensors = storeId => {
    this.getPopup('spinner', 'Fetching Named Sensors', null);
    API.getNamedSensors(storeId)
      .then(response => {
        this.setNamedSensors(response.data);
        FireboardUtils.addNetworkLogs(response);
      })
      .catch(error => this.getErrorPopup(error));
  };

  setNamedSensors = sensors => {
    this.setState({
      namedSensors: sensors,
      filteredNamedSensors: sensors,
      popup: undefined,
      sensorName: '',
      searchKey: '',
      selectedSensorGrpId: null,
    });
  };

  sortFunction = (a, b) => {
    const name1 = a.name ? a.name.toLowerCase() : '';
    const name2 = b.name ? b.name.toLowerCase() : '';
    if (name1 < name2) {
      return -1;
    }
    if (name1 > name2) {
      return 1;
    }
    return 0;
  };

  getPopup = (type, heading, message, callback) => {
    const popup =
      type === 'spinner'
        ? getPopupConfig('spinner')
        : getPopupConfig('error', () => {
          callback && callback();
          this.setState({ popup: undefined });
        });
    if (heading) {
      popup.heading = heading;
    }
    if (message) {
      popup.message = message;
    }
    this.setState({ popup });
  };

  closePopup = () => {
    this.setState({ popup: undefined });
  };

  getErrorPopup = (error) => {
    const errorMsg = error.response
      ? error.response.data
      : error.message;
    FireboardUtils.addNetworkLogs(error.response);
    this.getPopup('error', null, errorMsg);
  };

  goBack = () => {
    const { history } = this.props;
    history && history.goBack();
  };

  onChangeSensorGroup = e => {
    this.setState({ selectedSensorGrpId: e.target.value });
  };

  onChangeInput = key => event => {
    if (key === 'searchSensor') {
      const { namedSensors } = this.state;
      const value = (event.target.value).toLowerCase().trim();
      const filteredSensors = namedSensors.filter(sensor => {
        if ((sensor.name && String(sensor.name).toLowerCase().includes(value)) ||
          (sensor.externalId && String(sensor.externalId).toLowerCase().includes(value))
        ) {
          return sensor;
        }
      });
      this.setState({ filteredNamedSensors: filteredSensors, searchKey: event.target.value });
    } else if (key === 'sensorName') {
      this.setState({ [key]: event.target.value });
    }
  };

  openQRCodeScanner = () => {
    if (window.ReactNativeWebView) {
      EventEmitter.scanQRCode('scanQRCode');
    }
  };

  onQRCodeScanned = qrCode => {
    const callback = () => {
      this.getPopup('spinner', 'Adding Sensor', null);
      const { selectedSensorGrpId, sensorName } = this.state;
      const { reduxProps: { storeInfo } } = this.props;
      const { id: storeId } = storeInfo?.data || {};
      const payload = {
        storeId: storeId,
        externalId: FireboardUtils.getLastCodeFromQrCode(qrCode),
        sensorTypeId: 6,
        stationId: null,
        sensorGroupId: selectedSensorGrpId,
        name: sensorName.trim(),
      };
      API.registerSensor(payload)
        .then(response => {
          const { namedSensors } = this.state;
          this.setNamedSensors([...namedSensors, response.data]);
          FireboardUtils.addNetworkLogs(response);
        })
        .catch(error => this.getErrorPopup(error));
    };

    const { sensorName } = this.state;
    const popup = getPopupConfig('error');
    popup.heading = 'Confirmation';
    popup.message = `This will assign the sensor to name ${sensorName}. Do you want to continue?`;
    popup.buttons = [
      {
        text: 'NO',
        onClick: this.closePopup,
      },
      {
        text: 'YES',
        onClick: () => {
          callback && callback();
        }
      },
    ];
    this.setState({ popup });
  };

  onClickDeleteSensor = sensor => {
    this.deleteSensor(sensor);
  };

  deleteSensor = sensor => {
    const callback = () => {
      this.getPopup('spinner', 'Deleting Sensor', null);
      API.deRegisterSensor(sensor.id)
        .then(response => {
          const { namedSensors } = this.state;
          this.setNamedSensors(namedSensors.filter(s => s.id !== sensor.id));
          FireboardUtils.addNetworkLogs(response);
        })
        .catch(error => this.getErrorPopup(error));
    };

    const popup = getPopupConfig('error');
    popup.heading = 'Confirmation';
    popup.message = `This will delete the sensor named ${sensor.name}. Do you want to continue?`;
    popup.buttons = [
      {
        text: 'NO',
        onClick: this.closePopup,
      },
      {
        text: 'YES',
        onClick: () => {
          callback && callback();
        }
      },
    ];
    this.setState({ popup });
  };

  get viewProps() {
    const {
      popup,
      filteredNamedSensors,
      selectedSensorGrpId,
      sensorName,
      searchKey,
    } = this.state || {};

    const noSensorGroup = { id: null, name: "No Sensor Group" };
    const sensorGroups = [noSensorGroup, ...this.sensorGroupsList];
    return {
      popup,
      namedSensors: filteredNamedSensors,
      sensorName,
      searchKey,
      selectedSensorGrpId,
      sensorGroupsList: sensorGroups,
      onChangeSensorGroup: this.onChangeSensorGroup,
      onClickDeleteSensor: this.onClickDeleteSensor,
      goBack: this.goBack,
      closePopup: this.closePopup,
      sortFunction: this.sortFunction,
      openQRCodeScanner: this.openQRCodeScanner,
      onChangeInput: this.onChangeInput,
    };
  };

  render() {
    const viewProps = this.viewProps;
    return <View {...viewProps} />;
  }
}

const mapStateToProps = (reduxProps) => ({
  reduxProps,
});

export default connect(mapStateToProps, null)(NamedSensors);
